import React, { useCallback, useEffect } from 'react';
import Toolbar, { type ToolbarButton } from '../Toolbar';
import { ModelExplorer, EDG, OET, More, FSMS } from '../Icons';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { keyboard } from '../../lib/shortcuts';
import { useStores } from '../../hooks/useStores';

/**
 * The menu with project related actions.
 *
 * Renders:
 * - the buttons to show/hide views
 * - the dropdown with extra project actions
 *
 * The stores that used to be injected are read here from useStores():
 * - visibility: the UIVisibility store
 * - projectUI: the UIProjects store
 * - history: the version history for the active model, looked up by the `:id`
 *   route param (useParams) — this replaces the old
 *   `withRouter(inject(({ store }, props) => { ... props.match.params.id ... }))`.
 */
const ProjectMenu = observer(function ProjectMenu() {
  const store = useStores();
  const { id } = useParams();

  const visibility = store.ui.visible;
  const projectUI = store.ui.project;
  const project = store.projects.getProjectById(id as string);
  const history = project ? project.model!.history : [];

  // Are we on a small screen?
  const isSmallScreen = () => {
    return window.matchMedia('(max-width: 1400px)').matches;
  };

  // Toggle handlers. They only read `visibility` (a stable MST store), so they
  // can be memoised with an empty dep list: this gives them a stable identity
  // across renders so the keyboard add/remove below register and unregister the
  // exact same function references (Backbone's `.off` matches by reference).
  const handleEDGClick = useCallback(() => {
    if (isSmallScreen()) {
      visibility.showEDG();
    } else {
      visibility.toggleEDG();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOETClick = useCallback(() => {
    if (isSmallScreen()) {
      visibility.showOET();
    } else {
      visibility.toggleOET();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFSMsClick = useCallback(() => {
    if (isSmallScreen()) {
      visibility.showFSMs();
    } else {
      visibility.toggleFSMs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Register the keyboard shortcut listeners on mount, remove them on unmount
  // (was componentDidMount / componentWillUnmount). The handlers and the store
  // actions are stable references, so add and remove pair up exactly as the
  // class did.
  useEffect(() => {
    keyboard.addListener('e', handleEDGClick);
    keyboard.addListener('shift+e', visibility.showEDG);
    keyboard.addListener('o', handleOETClick);
    keyboard.addListener('shift+o', visibility.showOET);
    keyboard.addListener('f', handleFSMsClick);
    keyboard.addListener('shift+f', visibility.showFSMs);
    return () => {
      keyboard.removeListener('e', handleEDGClick);
      keyboard.removeListener('shift+e', visibility.showEDG);
      keyboard.removeListener('o', handleOETClick);
      keyboard.removeListener('shift+o', visibility.showOET);
      keyboard.removeListener('f', handleFSMsClick);
      keyboard.removeListener('shift+f', visibility.showFSMs);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Buttons in the dropdown with project actions.
   *
   * The handlers for these buttons only set a value in the UIProjects MST
   * store. The actual execution of the action, is handled by the ProjectActions
   * component (and its subcomponents).
   */
  const dropdownButtons = (): ToolbarButton[] => {
    return [
      {
        key: 'change_model_settings',
        text: 'Model settings',
        title: 'Change model settings',
        active: false,
        handler: () => {
          projectUI.setChangeSettings(true);
        },
      },
      {
        key: 'check_model',
        text: 'Check model',
        title: 'Check model and show report',
        active: false,
        handler: () => {
          projectUI.setCheckModel(true);
        },
      },
      {
        key: 'learning_report',
        text: 'Learning report',
        title: 'Generate learning report',
        active: false,
        handler: () => {
          projectUI.setLearningReport(true);
        },
      },
      {
        key: 'export_model',
        text: 'Export model',
        title: 'Export model to MXP-file',
        active: false,
        handler: () => {
          projectUI.setExportModel(true);
        },
      },
      {
        key: 'versions',
        text: 'History',
        title: 'Save a new version or restore an old one',
        handler: 'dropdown',
        dropdown: {
          arrow: false,
          buttons: [
            {
              key: 'version_save',
              text: 'Save new version',
              title: 'Save a new version',
              handler: () => {
                projectUI.setSaveVersion(true);
              },
            },
            ...history.map((v) => ({
              key: v.id,
              text: v.name,
              title: `Restore ${v.name} (${v.created.toLocaleString(DateTime.DATETIME_MED)})`,
              handler: () => {
                projectUI.setRestoreVersion(v.id);
              },
            })),
          ],
        },
      },
      {
        key: 'save_as',
        text: 'Save As ...',
        title: 'Save a copy of the model under a new name',
        active: false,
        handler: () => {
          projectUI.setSaveAs(true);
        },
      },
    ];
  };

  /**
   * The buttons in the menu
   */
  const buttons = (): ToolbarButton[] => {
    return [
      {
        key: 'model_explorer',
        icon: ModelExplorer,
        title: 'Open Model Explorer',
        active: visibility.modelExplorer,
        handler: visibility.toggleModelExplorer,
      },
      {
        key: 'edg',
        icon: EDG,
        title: 'Show EDG',
        active: visibility.edg,
        handler: handleEDGClick,
      },
      {
        key: 'oet',
        icon: OET,
        title: 'Show OET',
        active: visibility.oet,
        handler: handleOETClick,
      },
      {
        key: 'fsms',
        icon: FSMS,
        title: 'Show FSMs',
        active: visibility.fsms,
        handler: handleFSMsClick,
      },
      {
        key: 'more',
        icon: More,
        title: 'More',
        handler: 'dropdown',
        dropdown: {
          arrow: false,
          buttons: dropdownButtons(),
        },
      },
    ];
  };

  return <Toolbar variant="appbar" buttons={buttons()} />;
});

export default ProjectMenu;
